import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import { NAV_LINKS, IMAGES } from "../data/content";
import { subscribe } from "../lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribe({ email });
      toast.success(res.message || "Welcome to the Loft.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="main-footer" className="relative mt-0 bg-[#231233] text-[#F8F6F2]">
      <div className="container-loft py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img src={IMAGES.emblem} alt="emblem" className="h-12 w-12 object-contain" />
              <span className="font-serif text-2xl">London's Loft of Lyfe™</span>
            </div>
            <p className="text-[#F8F6F2]/70 leading-relaxed max-w-sm font-light">
              Healing lives. Illuminating paths. Elevating legacies. A sanctuary where care, strategy, and growth meet.
            </p>
            <div className="flex items-center gap-4 mt-6">
              {[Instagram, Linkedin, Youtube, Facebook].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  data-testid={`footer-social-${i}`}
                  className="h-10 w-10 rounded-full border border-white/20 grid place-items-center hover:bg-[#5B2C83] hover:border-[#5B2C83] transition-all duration-300"
                  aria-label="social link"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3 md:col-start-6">
            <h4 className="overline text-[#A8D8F0] mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-[#F8F6F2]/75 hover:text-white transition-colors text-sm">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="overline text-[#A8D8F0] mb-5">Join the Loft Community</h4>
            <p className="text-[#F8F6F2]/70 text-sm mb-4 font-light">
              Reflections on healing, strategy, and intentional living — delivered with care.
            </p>
            <form onSubmit={onSubscribe} className="flex items-center gap-2" data-testid="footer-subscribe-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-subscribe-input"
                className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#A8D8F0] transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-subscribe-button"
                className="h-12 w-12 rounded-full bg-[#0B7A5A] grid place-items-center hover:bg-[#096449] transition-colors disabled:opacity-60"
                aria-label="subscribe"
              >
                <Send size={17} />
              </button>
            </form>
          </div>
        </div>

        <div className="divider-line my-12 opacity-30" />
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#F8F6F2]/50">
          <p>© {new Date().getFullYear()} London's Loft of Lyfe™. All rights reserved.</p>
          <p className="font-serif italic text-sm text-[#F8F6F2]/60">Heal. Learn. Grow. Build legacy. Live intentionally.</p>
        </div>
      </div>
    </footer>
  );
}
